import { DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, convertToParamMap, NavigationExtras, ParamMap, Params, Router } from '@angular/router';
import { distinctUntilChanged, map } from 'rxjs';
import { APP } from '../config/app';
import { IBase } from './base';
import { ManagementQueryParam, ModalQueryParam } from './enums';
import { ManagementDetailState } from './managementDetailState';
import { ManagementListState } from './managementListState';
import { IPageChange } from './paginatedList';

export class ManagementRouteState<TSummary, TDetail extends IBase> {
  readonly queryParams = signal<ParamMap>(convertToParamMap({}));

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly listPath: string[],
    private readonly list: ManagementListState<TSummary>,
    private readonly detail: ManagementDetailState<TDetail>,
    private readonly loadDetail: (id: number) => Promise<TDetail>,
  ) {}

  start() {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((queryParams) => {
      this.queryParams.set(queryParams);
    });
    this.route.queryParamMap
      .pipe(
        distinctUntilChanged((previous, current) => this.listQueryKey(previous) === this.listQueryKey(current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((queryParams) => {
        this.list.searchValue.set(queryParams.get(ManagementQueryParam.SEARCH) ?? '');
        this.list.load(this.toPage(queryParams.get(ManagementQueryParam.PAGE)));
      });
    this.route.paramMap
      .pipe(
        map((params) => params.get(APP.MANAGEMENT_DETAIL_ID_PARAM)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detailId) => this.showRouteDetail(detailId));
  }

  search(searchValue: string) {
    this.updateQueryParams({ [ManagementQueryParam.SEARCH]: searchValue.trim() || null });
  }

  changePage(pageChange: IPageChange) {
    const currentPage = this.list.pagination().actualPage;
    const page = pageChange.specificPage ?? (pageChange.isNext ? currentPage + 1 : currentPage - 1);
    this.updateQueryParams({ [ManagementQueryParam.PAGE]: page > 1 ? page : null }, false);
  }

  changeFilter(queryParam: ManagementQueryParam, value: string | null, resetPage = true) {
    this.updateQueryParams({ [queryParam]: value || null }, resetPage);
  }

  queryParamValues(queryParam: ManagementQueryParam): string[] {
    const value = this.queryParams().get(queryParam);
    return value ? value.split(APP.MANAGEMENT_FILTER_VALUES_SEPARATOR) : [];
  }

  changeFilterValues(queryParam: ManagementQueryParam, values: string[]) {
    this.changeFilter(queryParam, values.join(APP.MANAGEMENT_FILTER_VALUES_SEPARATOR));
  }

  showDetail(id: number) {
    this.navigateToDetail(String(id));
  }

  showCreation() {
    this.navigateToDetail(APP.MANAGEMENT_NEW_DETAIL_ID);
  }

  closeDetail(replaceUrl = false) {
    this.router.navigate(this.listPath, { queryParamsHandling: 'preserve', replaceUrl });
  }

  showSaved(savedDetail: TDetail) {
    this.detail.showSaved(savedDetail);
    this.list.load();
    if (this.route.snapshot.paramMap.get(APP.MANAGEMENT_DETAIL_ID_PARAM) === APP.MANAGEMENT_NEW_DETAIL_ID) {
      this.navigateToDetail(String(savedDetail.id), { replaceUrl: true });
    }
  }

  private showRouteDetail(detailId: string | null) {
    if (!detailId) return this.detail.close();
    if (detailId === APP.MANAGEMENT_NEW_DETAIL_ID) return this.detail.startCreation();
    const id = Number(detailId);
    if (!Number.isInteger(id)) return this.closeDetail(true);
    if (this.detail.selected()?.id === id) return;
    this.detail.select(() => this.loadDetail(id)).catch(() => this.closeDetail(true));
  }

  private navigateToDetail(detailId: string, extras: NavigationExtras = {}) {
    this.router.navigate([...this.listPath, detailId], { queryParamsHandling: 'preserve', ...extras });
  }

  private updateQueryParams(queryParams: Params, resetPage = true) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: resetPage ? { ...queryParams, [ManagementQueryParam.PAGE]: null } : queryParams,
      queryParamsHandling: 'merge',
    });
  }

  private listQueryKey(queryParams: ParamMap) {
    return queryParams.keys
      .filter((key) => !APP.MANAGEMENT_DETAIL_QUERY_PARAMS.includes(key as ManagementQueryParam))
      .filter((key) => !Object.values<string>(ModalQueryParam).includes(key))
      .sort()
      .map((key) => `${key}=${queryParams.get(key)}`)
      .join('&');
  }

  private toPage(page: string | null) {
    const pageNumber = Number(page);
    return Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1;
  }
}
